'use strict';

/**
 * TN Multi Collection Slider
 *
 * MEASUREMENT NOTE
 *
 * Slide width is set entirely in CSS (flex-basis percentages plus a fixed
 * margin-right), so it can only change when the container changes size - never
 * as a result of clicking an arrow or starting a drag. Measuring it from inside
 * those handlers read a layout property immediately after setTrack() had
 * written track.style.transform, forcing a synchronous style recalc and layout
 * on every interaction (~234ms of Recalculate Style in a profile of the TWN
 * home page).
 *
 * A first pass moved those reads to a single call at init. That did not help:
 * running getBoundingClientRect() at DOMContentLoaded, before the first layout
 * has happened, forces a full-document style and layout pass - once per slider
 * on the page - and cost almost exactly the same (~231ms).
 *
 * There is now NO synchronous measurement on the normal path at all. A
 * ResizeObserver on the first slide supplies the width. Its first callback
 * fires shortly after observation begins, carrying dimensions the browser has
 * already computed as part of its own layout pass, so reading them is free.
 * slideW is cached from there and reused by every interaction.
 *
 * The synchronous fallback is retained only for browsers without
 * ResizeObserver, where there is no alternative.
 */
var TNMultiCollectionSlider = (function () {

  var _initialised = {};

  function init(sectionId) {

    if (_initialised[sectionId]) return;
    _initialised[sectionId] = true;

    var wrap    = document.getElementById('tnnursery-mc-wrap-' + sectionId);
    var track   = document.getElementById('tnnursery-mc-track-' + sectionId);
    var prevBtn = document.getElementById('tnnursery-mc-prev-' + sectionId);
    var nextBtn = document.getElementById('tnnursery-mc-next-' + sectionId);
    var dotsEl  = document.getElementById('tnnursery-mc-dots-' + sectionId);

    if (!wrap || !track) return;

    var slides      = Array.prototype.slice.call(track.querySelectorAll('.tnnursery-mc-slide'));
    var totalSlides = slides.length;
    var currentIndex = 0;
    var slideW       = 0;   // populated by the ResizeObserver — see MEASUREMENT NOTE

    // ── drag state ───────────────────────────────────────────────
    var dragStartX   = 0;
    var dragStartY   = 0;
    var dragDeltaX   = 0;
    var dragging     = false;
    var dragLocked   = false;   // locked to vertical scroll — ignore this gesture
    var baseOffset   = 0;       // translateX at drag start

    // ── helpers ──────────────────────────────────────────────────
    // window.innerWidth is a viewport property, not an element property, so
    // reading it does not force layout the way offsetWidth does.
    function isMobile() { return window.innerWidth <= 768; }

    function gapSize() { return isMobile() ? 8 : 16; }

    function getVisibleCount() {
      var w = window.innerWidth;
      if (w <= 768)  return 2;
      if (w <= 1024) return 3;
      return 4;
    }

    function maxIndex() {
      return Math.max(0, totalSlides - getVisibleCount());
    }

    // set transform — pass `animate` false to skip CSS transition
    function setTrack(px, animate) {
      if (animate) {
        track.style.transition = 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      } else {
        track.style.transition = 'none';
      }
      track.style.transform = 'translateX(' + (-px) + 'px)';
    }

    function goToSlide(index) {
      // slideW is 0 only in the sliver between init and the observer's first
      // callback. Updating the index without moving the track keeps state
      // consistent; the observer's callback then aligns the track correctly.
      currentIndex = Math.max(0, Math.min(index, maxIndex()));
      updateDots();
      updateArrows();
      if (!slideW) return;
      baseOffset = slideW * currentIndex;
      setTrack(baseOffset, true);
    }

    // ── dots / arrows ─────────────────────────────────────────────
    function buildDots() {
      if (!dotsEl) return;
      dotsEl.innerHTML = '';
      var count = maxIndex() + 1;
      for (var i = 0; i < count; i++) {
        var btn = document.createElement('button');
        btn.className   = 'tnnursery-mc-dot' + (i === 0 ? ' tnnursery-mc-dot--active' : '');
        btn.setAttribute('data-index', i);
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-label', 'Slide ' + (i + 1));
        (function (idx) {
          btn.addEventListener('click', function () { goToSlide(idx); });
        }(i));
        dotsEl.appendChild(btn);
      }
    }

    function updateDots() {
      if (!dotsEl) return;
      dotsEl.querySelectorAll('.tnnursery-mc-dot').forEach(function (dot, i) {
        var active = i === currentIndex;
        dot.classList.toggle('tnnursery-mc-dot--active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    }

    function updateArrows() {
      if (prevBtn) prevBtn.toggleAttribute('disabled', currentIndex <= 0);
      if (nextBtn) nextBtn.toggleAttribute('disabled', currentIndex >= maxIndex());
    }

    // ── size tracking ─────────────────────────────────────────────
    var hasResizeObserver = typeof ResizeObserver === 'function';

    if (hasResizeObserver && slides[0]) {
      var lastVisibleCount = getVisibleCount();
      var firstMeasurement = true;

      var slideObserver = new ResizeObserver(function (entries) {
        var entry = entries[0];
        if (!entry) return;

        // borderBoxSize matches offsetWidth semantics (includes padding and
        // border); contentRect does not, so it is only the fallback.
        var width;
        if (entry.borderBoxSize && entry.borderBoxSize.length) {
          width = entry.borderBoxSize[0].inlineSize;
        } else {
          width = entry.contentRect.width;
        }
        if (!width) return;

        slideW = width + gapSize();

        if (firstMeasurement) {
          // Initial callback: the track is already at translateX(0) and
          // currentIndex is 0, so there is nothing to re-align. Writing a
          // transform here would only dirty layout for no reason.
          firstMeasurement = false;
          return;
        }

        // Crossing a breakpoint changes how many slides fit, which changes the
        // dot count and can leave currentIndex past the new maximum.
        var visibleNow = getVisibleCount();
        if (visibleNow !== lastVisibleCount) {
          lastVisibleCount = visibleNow;
          buildDots();
          goToSlide(0);
        } else {
          // Same breakpoint, new width: keep the current slide aligned.
          baseOffset = slideW * currentIndex;
          setTrack(baseOffset, false);
        }
      });

      slideObserver.observe(slides[0]);
    }

    // ── arrow clicks ──────────────────────────────────────────────
    // No measurement here. slideW is already current — re-reading it after the
    // previous interaction wrote a transform is what forced the reflow.
    if (prevBtn) prevBtn.addEventListener('click', function (e) {
      e.stopPropagation(); goToSlide(currentIndex - 1);
    });
    if (nextBtn) nextBtn.addEventListener('click', function (e) {
      e.stopPropagation(); goToSlide(currentIndex + 1);
    });

    // ── touch drag ────────────────────────────────────────────────
    wrap.addEventListener('touchstart', function (e) {
      dragStartX  = e.touches[0].clientX;
      dragStartY  = e.touches[0].clientY;
      dragDeltaX  = 0;
      dragging    = false;
      dragLocked  = false;
      baseOffset  = slideW * currentIndex;
    }, { passive: true });

    wrap.addEventListener('touchmove', function (e) {
      if (dragLocked) return;   // vertical scroll — hands off
      if (!slideW) return;      // not measured yet — let the page scroll

      var dx = dragStartX - e.touches[0].clientX;
      var dy = dragStartY - e.touches[0].clientY;

      // first significant move: decide axis
      if (!dragging) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;   // too small to decide
        if (Math.abs(dy) > Math.abs(dx)) {
          dragLocked = true;    // user is scrolling vertically — release control
          return;
        }
        dragging = true;
      }

      // horizontal drag — prevent page scroll only now
      e.preventDefault();

      dragDeltaX = dx;
      var liveOffset = baseOffset + dragDeltaX;

      // rubber-band at edges
      var min = 0;
      var max = slideW * maxIndex();
      if (liveOffset < min) liveOffset = min - (min - liveOffset) * 0.25;
      if (liveOffset > max) liveOffset = max + (liveOffset - max) * 0.25;

      setTrack(liveOffset, false);   // no animation — follow finger exactly
    }, { passive: false });           // ← must be non-passive to call preventDefault

    wrap.addEventListener('touchend', function (e) {
      if (!dragging) return;
      dragging   = false;
      dragLocked = false;

      var dx        = dragDeltaX;
      var threshold = slideW * 0.2;   // 20 % of slide width to trigger snap

      var targetIndex = currentIndex;
      if (dx > threshold)       targetIndex = currentIndex + 1;
      else if (dx < -threshold) targetIndex = currentIndex - 1;

      goToSlide(targetIndex);   // snaps with CSS transition
    }, { passive: true });

    // ── fallback for browsers without ResizeObserver ──────────────
    // This is the only path that measures synchronously. It is deferred to the
    // load event so it never lands during initial parse, and debounced so a
    // drag-resize cannot thrash.
    if (!hasResizeObserver) {
      var measureSyncFallback = function () {
        if (!slides[0]) return;
        slideW = slides[0].getBoundingClientRect().width + gapSize();
      };

      var runFallback = function () {
        measureSyncFallback();
        baseOffset = slideW * currentIndex;
        setTrack(baseOffset, false);
      };

      if (document.readyState === 'complete') {
        setTimeout(runFallback, 0);
      } else {
        window.addEventListener('load', runFallback, { once: true });
      }

      var resizeTimer;
      var lastWidth = window.innerWidth;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          if (window.innerWidth === lastWidth) return;
          lastWidth = window.innerWidth;
          measureSyncFallback();
          goToSlide(0);
          buildDots();
        }, 150);
      }, { passive: true });
    }

    // ── init ──────────────────────────────────────────────────────
    // Nothing here reads layout. buildDots and updateArrows depend only on
    // window.innerWidth and the slide count.
    buildDots();
    updateArrows();
  }

  return { init: init };

})();

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('[id^="tnnursery-mc-wrap-"]').forEach(function (el) {
    var sectionId = el.id.replace('tnnursery-mc-wrap-', '');
    TNMultiCollectionSlider.init(sectionId);
  });
});